import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * Error Boundary Component
 * Catches errors in v2 components and falls back to legacy or error UI
 * Ensures application doesn't crash if new components have issues
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const errorCount = this.state.errorCount + 1;
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught error:', error, errorInfo);
    }
    
    // Log to error tracking service (e.g., Sentry) in production
    if (process.env.NODE_ENV === 'production' && window.Sentry) {
      window.Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack
          }
        }
      });
    }
    
    // Update state with error details
    this.setState({
      error,
      errorInfo,
      errorCount
    });
    
    // If too many errors, fall back to legacy component
    if (errorCount > 3 && this.props.fallbackComponent) {
      console.warn('Too many errors in v2 component, falling back to legacy');
      // Force use of legacy component by clearing feature flag
      if (this.props.featureFlagName) {
        localStorage.setItem(`feature_${this.props.featureFlagName}`, 'false');
      }
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleUseLegacy = () => {
    if (this.props.featureFlagName) {
      localStorage.setItem(`feature_${this.props.featureFlagName}`, 'false');
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      // If a fallback component is provided and error count is high, use it
      if (this.props.fallbackComponent && this.state.errorCount > 2) {
        const FallbackComponent = this.props.fallbackComponent;
        return <FallbackComponent {...this.props.fallbackProps} />;
      }
      
      // Otherwise show error UI
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            p: 3
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 500,
              textAlign: 'center'
            }}
          >
            <ErrorOutlineIcon 
              sx={{ 
                fontSize: 60, 
                color: 'error.main',
                mb: 2
              }} 
            />
            <Typography variant="h5" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              We encountered an error while loading this component. 
              {this.props.featureFlagName && (
                " You can switch back to the classic version if this persists."
              )}
            </Typography>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box 
                sx={{ 
                  mt: 2, 
                  p: 2, 
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  textAlign: 'left'
                }}
              >
                <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace' }}>
                  {this.state.error.toString()}
                </Typography>
              </Box>
            )}
            
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                onClick={this.handleReset}
              >
                Try Again
              </Button>
              {this.props.featureFlagName && (
                <Button 
                  variant="outlined" 
                  onClick={this.handleUseLegacy}
                >
                  Use Classic Version
                </Button>
              )}
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;